import React, { useState, useRef, useEffect } from 'react';
import { Mic, ClipboardList, Clock, FileText, Pause, CheckCircle, AlertTriangle, Circle, Square, RefreshCw, Lightbulb, Play } from 'lucide-react';
import '../../styles/wizard-steps.css';

interface AudioRecordingStepProps {
  consentText: string;
  onNext: (audioBlob: Blob, duration: number) => void;
  onBack: () => void;
  minimumDuration?: number; // in seconds
}

const AudioRecordingStep: React.FC<AudioRecordingStepProps> = ({
  consentText,
  onNext,
  onBack,
  minimumDuration = 5
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);

  const audioRef = useRef<HTMLAudioElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      stopMicrophone();
    };
  }, []);

  const stopMicrophone = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Setup audio level visualization
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 256;
      source.connect(analyzer);
      analyzerRef.current = analyzer;

      const updateAudioLevel = () => {
        const dataArray = new Uint8Array(analyzer.frequencyBinCount);
        analyzer.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 255 * 100);
        animationRef.current = requestAnimationFrame(updateAudioLevel);
      };
      updateAudioLevel();

      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        
        if (audioRef.current) {
          audioRef.current.src = URL.createObjectURL(blob);
        }
        
        stopMicrophone();
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setDuration(0);
      setError('');

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError('Could not access microphone. Please grant microphone permissions.');
      console.error('Microphone error:', err);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const retakeAudio = () => {
    setRecordedBlob(null);
    setDuration(0);
    setError('');
    setAudioLevel(0);
  };

  const handleContinue = () => {
    if (recordedBlob && duration >= minimumDuration) {
      onNext(recordedBlob, duration);
    } else {
      setError(`Recording must be at least ${minimumDuration} seconds`);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const canContinue = recordedBlob !== null && duration >= minimumDuration;

  return (
    <div className="wizard-step audio-step">
      <div className="step-header">
        <div className="step-icon"><Mic size={24} /></div>
        <h2 className="step-title">Audio Consent</h2>
        <p className="step-subtitle">Record your verbal consent</p>
      </div>

      <div className="audio-instructions">
        <h3><ClipboardList size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Instructions:</h3>
        <ol>
          <li>Start recording</li>
          <li>Say "I Agree" clearly before submitting</li>
        </ol>
        <p className="requirement-text">
          <Clock size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Minimum recording duration: {minimumDuration} seconds
        </p>
      </div>

      <div className="consent-text-box">
        <h4><FileText size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Please read this statement:</h4>
        <div className="consent-text-content">
          "{consentText}"
        </div>
      </div>

      <div className="audio-visualizer">
        {isRecording && !isPaused && (
          <>
            <div className="audio-level-bar">
              <div 
                className="audio-level-fill" 
                style={{ width: `${audioLevel}%` }}
              />
            </div>
            <div className="recording-indicator">
              <span className="recording-dot"></span>
              <span>Recording: {formatDuration(duration)}</span>
            </div>
          </>
        )}
        
        {isPaused && (
          <div className="paused-indicator">
            <span><Pause size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Paused: {formatDuration(duration)}</span>
          </div>
        )}

        {recordedBlob && (
          <div className="audio-playback">
            <audio ref={audioRef} controls className="audio-player" />
            <p className="success-text">
              <CheckCircle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Recorded: {formatDuration(duration)}
            </p>
          </div>
        )}

        {!isRecording && !recordedBlob && (
          <div className="audio-idle">
            <div className="microphone-icon"><Mic size={24} /></div>
            <p>Ready to record</p>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <AlertTriangle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> {error}
        </div>
      )}

      <div className="audio-controls">
        {!recordedBlob ? (
          <>
            {!isRecording ? (
              <button onClick={startRecording} className="btn btn-danger">
                <Circle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Start Recording
              </button>
            ) : (
              <>
                {!isPaused ? (
                  <button onClick={pauseRecording} className="btn btn-warning">
                    <Pause size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Pause
                  </button>
                ) : (
                  <button onClick={resumeRecording} className="btn btn-success">
                    <Play size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Resume
                  </button>
                )}
                <button onClick={stopRecording} className="btn btn-secondary">
                  <Square size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Stop
                </button>
              </>
            )}
          </>
        ) : (
          <button onClick={retakeAudio} className="btn btn-secondary">
            <RefreshCw size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Retake
          </button>
        )}
      </div>

      <div className="step-actions">
        <button className="btn-link" onClick={onBack} disabled={isRecording}>
          ← Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="btn btn-primary"
        >
          {canContinue ? 'Continue →' : 'Recording Required'}
        </button>
      </div>

      <div className="help-text">
        <p><Lightbulb size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Speak clearly into your microphone</p>
      </div>
    </div>
  );
};

export default AudioRecordingStep;
